import { create } from "zustand";
import type {
  RecurringExpenseItem,
  RecurringExpenseSummary,
} from "@boo/shared";
import {
  createRecurringExpense,
  updateRecurringExpense,
  deactivateRecurringExpense,
  fetchRecurringExpenses,
  fetchRecurringExpenseSummary,
  type CreateRecurringExpensePayload,
  type UpdateRecurringExpensePayload,
} from "../../../services/recurringExpenseApi";
import type { RecurringExpenseSort } from "../types/recurring-expense.types";

interface RecurringExpenseState {
  items: RecurringExpenseItem[];
  monthlyTotal: number;
  yearlyTotal: number;
  sort: RecurringExpenseSort;
  includeInactive: boolean;
  loading: boolean;
  error: string | null;
  summary: RecurringExpenseSummary | null;
  summaryLoading: boolean;
  loadExpenses: () => Promise<void>;
  loadSummary: () => Promise<void>;
  createExpense: (dto: CreateRecurringExpensePayload) => Promise<void>;
  updateExpense: (
    id: string,
    dto: UpdateRecurringExpensePayload,
  ) => Promise<void>;
  deactivateExpense: (id: string) => Promise<void>;
  setSort: (sort: RecurringExpenseSort) => void;
  setIncludeInactive: (v: boolean) => void;
}

export const useRecurringExpenseStore = create<RecurringExpenseState>()(
  (set, get) => ({
    items: [],
    monthlyTotal: 0,
    yearlyTotal: 0,
    sort: "dueDate",
    includeInactive: false,
    loading: false,
    error: null,
    summary: null,
    summaryLoading: false,

    async loadSummary() {
      set({ summaryLoading: true });
      try {
        const data = await fetchRecurringExpenseSummary();
        set({ summary: data });
      } catch {
        // non-critical — widget will show empty state
      } finally {
        set({ summaryLoading: false });
      }
    },

    async createExpense(dto: CreateRecurringExpensePayload) {
      const prev = {
        items: get().items,
        monthlyTotal: get().monthlyTotal,
        yearlyTotal: get().yearlyTotal,
      };
      try {
        await createRecurringExpense(dto);
        await get().loadExpenses();
      } catch (err) {
        set(prev);
        throw err;
      }
    },

    async updateExpense(id: string, dto: UpdateRecurringExpensePayload) {
      const prev = {
        items: get().items,
        monthlyTotal: get().monthlyTotal,
        yearlyTotal: get().yearlyTotal,
      };
      try {
        await updateRecurringExpense(id, dto);
        await get().loadExpenses();
      } catch (err) {
        set(prev);
        throw err;
      }
    },

    async deactivateExpense(id: string) {
      // Optimistically remove from active list
      set((s) => ({ items: s.items.filter((i) => i.id !== id) }));
      try {
        await deactivateRecurringExpense(id);
        await get().loadExpenses();
      } catch (err) {
        await get().loadExpenses(); // revert on failure by reloading
        throw err;
      }
    },

    async loadExpenses() {
      const { sort, includeInactive } = get();
      set({ loading: true, error: null });
      try {
        const data = await fetchRecurringExpenses({ sort, includeInactive });
        set({
          items: data.items,
          monthlyTotal: data.monthlyTotal,
          yearlyTotal: data.yearlyTotal,
        });
      } catch (err) {
        set({
          error:
            err instanceof Error
              ? err.message
              : "Failed to load recurring expenses",
        });
      } finally {
        set({ loading: false });
      }
    },

    setSort(sort) {
      set({ sort });
      get().loadExpenses();
    },

    setIncludeInactive(v) {
      set({ includeInactive: v });
      get().loadExpenses();
    },
  }),
);
